import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  MyPatternListProps,
  RecommendFilter,
} from '../../../interface/aiAnalyze/aiRecommend/type';
import { Region, RegionProps, SidoList } from '../../search/FilterData';
import RegionSelectModal from '../../components/filterModal/RegionSelectModal';
import ItemDetailModal from '../myAlgorithm/pattern/ItemDetailModal';

interface Props {
  selectedItem: MyPatternListProps;
  filter: RecommendFilter;
  setFilter: (f: RecommendFilter) => void;
}

function FilterBox({ selectedItem, filter, setFilter }: Props) {
  const [selectedBtns, setSelectedBtns] = useState<Set<string>>(new Set());
  const [regionModalOn, setRegionModalOn] = useState(false);
  const [itemModalOn, setItemModalOn] = useState(false);

  const [sido, setSido] = useState('');
  const [sigungu, setSigungu] = useState('');
  const [emd, setEmd] = useState('');
  const [sigunguList, setSigunguList] = useState<RegionProps[]>([]);
  const [emdList, setEmdList] = useState<RegionProps[]>([]);
  const [currentRegion, setCurrentRegion] = useState<Region>({
    sido: '',
    sigungu: '',
    emd: '',
  });
  const [minPriceText, setMinPriceText] = useState(filter.minPrice);
  const [maxPriceText, setMaxPriceText] = useState('');

  const [isMinFocus, setIsMinFocus] = useState(false);
  const [isMaxFocus, setIsMaxFocus] = useState(false);

  const toggleBtn = (name: string) => {
    setSelectedBtns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(name)) {
        newSet.delete(name); // 선택 해제
      } else {
        newSet.add(name); // 선택 추가
      }
      return newSet;
    });
  };

  const handleChangeRegion = (r: {
    pnu: string;
    sido: string;
    sigungu: string;
    emd: string;
  }) => {
    setFilter({
      ...filter,
      pnu: r.pnu,
    });
    setCurrentRegion({
      sido: r.sido,
      sigungu: r.sigungu,
      emd: r.emd,
    });
  };

  useEffect(() => {
    const code = selectedItem.pnu.slice(0, 2);
    setSido(code);
    const label = SidoList.find((i) => i.code === code)?.region;
    setCurrentRegion({
      sido: label ?? '',
      sigungu: '',
      emd: '',
    });

    setFilter({
      ...filter,
      pnu: code,
      category: selectedItem.category,
      categoryDetail: selectedItem.category_detail,
    });
  }, [selectedItem]);

  const formatKoreanUnit = (value: number) => {
    const eok = Math.floor(value / 10000); // 억
    const man = value % 10000; // 만원

    if (eok > 0 && man > 0) return `${eok}억원 ${man}만원`;
    if (eok > 0) return `${eok}억원`;
    return `${man}만원`;
  };

  return (
    <Container>
      <Title>필터</Title>
      <Filter>
        <FilterChipWrapper>
          {['지역', '물건 종류', '가격'].map((label) => (
            <FilterChip
              key={label}
              $active={selectedBtns.has(label)}
              onClick={() => toggleBtn(label)}
            >
              {label}
            </FilterChip>
          ))}
        </FilterChipWrapper>
        <ResetButton
          onClick={() => {
            setSigungu('');
            setEmd('');
            setCurrentRegion((prev) => ({
              ...prev,
              sigungu: '',
              emd: '',
            }));
            setMinPriceText('');
            setMaxPriceText('');
            setFilter({
              ...filter,
              pnu: sido,
              categoryDetail: '',
              minPrice: '',
              maxPrice: '',
            });
          }}
        >
          <img src="/assets/images/icon/reset.svg" alt="" />
          초기화
        </ResetButton>
      </Filter>
      <OptionList>
        {selectedBtns.has('지역') && (
          <Section>
            <Option $active={!!currentRegion.sido}>
              {currentRegion.sido ? currentRegion.sido : '지역'}
            </Option>
            <DetailOption
              onClick={() => setRegionModalOn(true)}
              $active={!!currentRegion.sigungu}
            >
              {!!currentRegion.sigungu
                ? `${currentRegion.sigungu} ${currentRegion.emd}`
                : '상세 지역'}
            </DetailOption>
          </Section>
        )}
        {selectedBtns.has('물건 종류') && (
          <Section>
            <Option $active={!!filter.category}>
              {filter.category ? filter.category : '종류'}
            </Option>
            <DetailOption
              onClick={() => setItemModalOn(true)}
              $active={!!filter.categoryDetail}
            >
              {filter.categoryDetail ? filter.categoryDetail : '물건 종류'}
            </DetailOption>
          </Section>
        )}
        {selectedBtns.has('가격') && (
          <Section>
            <NunberInputWrap>
              <InputBox>
                <NumberInput
                  type="number"
                  pattern="[0-9]*"
                  placeholder="가격"
                  value={minPriceText}
                  onChange={(e) => {
                    const val = e.target.value.replace(/,/g, '');
                    if (val.length <= 8) {
                      setMinPriceText(val);
                    }
                  }}
                  onFocus={() => setIsMinFocus(true)}
                  onBlur={() => {
                    const parsed = Number(minPriceText);
                    setFilter({
                      ...filter,
                      minPrice: String(parsed * 10000),
                    });
                    setIsMinFocus(false);
                  }}
                />
                <span style={{ whiteSpace: 'nowrap' }}>만원 이상</span>
              </InputBox>
              {filter.minPrice && !isMinFocus && (
                <InputDescription
                  style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}
                >
                  {formatKoreanUnit(Number(filter.minPrice) / 10000)}
                </InputDescription>
              )}
            </NunberInputWrap>
            <span style={{ padding: '10px 0px' }}>~</span>
            <NunberInputWrap>
              <InputBox>
                <NumberInput
                  type="number"
                  pattern="[0-9]*"
                  placeholder="가격"
                  value={maxPriceText}
                  onChange={(e) => {
                    const val = e.target.value.replace(/,/g, '');
                    if (val.length <= 8) {
                      setMaxPriceText(val);
                    }
                  }}
                  onFocus={() => setIsMaxFocus(true)}
                  onBlur={() => {
                    const parsed = Number(maxPriceText);
                    setFilter({
                      ...filter,
                      maxPrice: String(parsed * 10000),
                    });
                    setIsMaxFocus(false);
                  }}
                />
                <span style={{ whiteSpace: 'nowrap' }}>만원 이하</span>
              </InputBox>
              {filter.maxPrice && !isMaxFocus && (
                <InputDescription
                  style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}
                >
                  {formatKoreanUnit(Number(filter.maxPrice) / 10000)}
                </InputDescription>
              )}
            </NunberInputWrap>
          </Section>
        )}

        {regionModalOn && (
          <RegionSelectModal
            initialRegion={{
              pnu: filter.pnu,
              ...currentRegion,
            }}
            onSelectRegion={handleChangeRegion}
            onClose={() => setRegionModalOn(false)}
          />
        )}

        {itemModalOn && (
          <ItemDetailModal
            category={filter.category}
            selected={filter.categoryDetail}
            onApply={(val) => {
              setFilter({
                ...filter,
                categoryDetail: val,
              });
            }}
            onClose={() => setItemModalOn(false)}
          />
        )}
      </OptionList>
    </Container>
  );
}

export default FilterBox;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const Title = styled.div`
  color: #222;
  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;
`;

const Filter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const FilterChipWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterChip = styled.div<{ $active?: boolean }>`
  display: flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 9999px;
  border: 1px solid #e5e8ea;
  background: #fff;

  color: #989ba2;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;

  ${({ $active }) =>
    $active &&
    `
  background: #222;
  color: #fff;
`}
`;

const ResetButton = styled.button`
  border: none;
  outline: none;
  background-color: transparent;

  display: flex;
  padding: 8px 0;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  color: #616161;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;
`;

const Section = styled.div`
  display: flex;
  padding: 8px 0;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const Option = styled.div<{ $active?: boolean }>`
  display: flex;
  height: 40px;
  padding: 8px 12px;
  align-items: center;
  border-radius: 8px;
  background: #f7f8fc;

  color: ${({ $active }) => ($active ? '#222' : 'rgba(55, 56, 60, 0.61)')};
  font-size: 14px;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;
`;

const DetailOption = styled(Option)`
  flex: 1 0 0;
`;

const NunberInputWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InputBox = styled.div<{ $disabled?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  height: 40px;
  border-radius: 8px;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f7f8fc;

  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  color: #616161;
  align-items: center;

  background: ${({ $disabled }) => ($disabled ? '#FAFAFB' : 'F7F8FC')};
  color: ${({ $disabled }) =>
    $disabled ? 'rgba(55, 56, 60, 0.16)' : 'rgba(55, 56, 60, 0.61)'};

  &:focus-within {
    border: 1px solid #008aff;
  }
`;

const NumberInput = styled.input`
  width: 100%;
  border: none;
  background-color: transparent;
  font-size: 14px;
  font-weight: 500;
  box-sizing: border-box;

  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &:focus {
    outline: none;
  }
  &:disabled::placeholder {
    color: rgba(55, 56, 60, 0.16);
  }
`;

const InputDescription = styled.div`
  padding: 0 12px;
  color: #222;
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;
